import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from 'react';
import { onAuthStateChanged, signInWithCustomToken, getIdTokenResult, type User } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from './firebase';

export interface LabOSUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  role: string;
  isAdmin: boolean;
  roles?: string[]; // Additional roles from LabOS API
}

interface LabOSState {
  user: LabOSUser | null;
  firebaseUser: User | null;
  config: Record<string, unknown>;
  loading: boolean;
  signOut: () => Promise<void>;
}

const Ctx = createContext<LabOSState | null>(null);

// LabOS base URL loaded from environment variables.
// For deployment: Set the LABOS_URL repository variable in GitHub (per environment, since dev/prod point at different LabOS instances).
// For local development: Create frontend/.env.local with VITE_LABOS_URL.
const LABOS_URL = import.meta.env.VITE_LABOS_URL;

if (!LABOS_URL) {
  throw new Error(
    'LabOS URL missing. Ensure the VITE_LABOS_URL environment variable is set:\n' +
      '  For local dev: Create frontend/.env.local\n' +
      '  For deployment: Set LABOS_URL in the GitHub environment (dev/prod)',
  );
}

// Which environment this build was deployed to (dev/prod), so the topbar can
// show it. Set via VITE_ENVIRONMENT in the deploy workflow's .env.production
// (the workflow already knows its target GitHub Environment name). Local
// runs don't go through that workflow, so fall back to Vite's own dev/build
// mode instead.
const ENVIRONMENT = (import.meta.env.VITE_ENVIRONMENT || (import.meta.env.DEV ? 'dev' : 'prod')).toLowerCase();

export function LabOSAuth({ children, appId, fallback }: { children: ReactNode; appId: string; fallback?: ReactNode }) {
  const [state, setState] = useState<Omit<LabOSState, 'signOut'>>({
    user: null,
    firebaseUser: null,
    config: {},
    loading: true,
  });
  const isHandshakeCodeProccessing = useRef(false);

  useEffect(() => {
    // Check for handshake code in URL (redirect from LabOS)
    const params = new URLSearchParams(window.location.search);
    const handshakeCode = params.get('handshake');

    if (handshakeCode) {
      // Redeem the handshake code for a Firebase custom token
      const redeem = httpsCallable<{ code: string }, { customToken: string }>(functions, 'redeemHandshakeCode');
      isHandshakeCodeProccessing.current = true;
      redeem({ code: handshakeCode })
        .then(async (result) => {
          await signInWithCustomToken(auth, result.data.customToken);
        })
        .catch((err) => {
          console.error('[LabOS] Handshake redemption failed:', err);
        })
        .finally(() => {
          isHandshakeCodeProccessing.current = false;
          // Clean up URL
          params.delete('handshake');
          params.delete('origin');
          const clean = window.location.pathname + (params.toString() ? '?' + params : '');
          window.location.href = clean;
        });
    }

    return onAuthStateChanged(auth, async (fbUser) => {
      if (isHandshakeCodeProccessing.current) return;
      if (!fbUser) {
        // Not authenticated — redirect to LabOS
        const returnUrl = encodeURIComponent(window.location.href);
        window.location.href = `${LABOS_URL}/login?return=${returnUrl}&app=${appId}`;
        return;
      }

      const token = await getIdTokenResult(fbUser);
      const role = (token.claims.role as string) || 'user';

      // Fetch runtime config from LabOS (group-level app parameters)
      let config: Record<string, unknown> = {};
      try {
        const getAppConfig = httpsCallable<{ appId: string }, Record<string, unknown>>(functions, 'getAppConfig');
        const result = await getAppConfig({ appId });
        config = result.data || {};
      } catch {
        /* config is optional */
      }

      // Fetch user roles from LabOS via Firebase callable function
      let userRoles: string[] = [];
      try {
        console.log('[LabOS] Fetching user roles via adminGetUserAppRoles...');

        interface UserRoleObject {
          id: string;
          role_key: string;
          role_name: string;
          description: string;
          permissions: Record<string, unknown>;
          assigned_at: Record<string, unknown>;
          assigned_by: string;
        }

        const adminGetUserAppRoles = httpsCallable<{ uid: string; appId: string }, UserRoleObject[]>(
          functions,
          'adminGetUserAppRoles',
        );

        const result = await adminGetUserAppRoles({ uid: fbUser.uid, appId });
        console.log('[LabOS] Raw roles response:', result.data);

        // Extract role_key from each role object
        userRoles = Array.isArray(result.data) ? result.data.map((role) => role.role_key) : [];
        console.log('[LabOS] User roles fetched successfully:', userRoles);
      } catch (err) {
        console.error('[LabOS] Error fetching user roles:', err);
      }

      // Sync the app-specific custom claims Firestore rules read. The backend
      // looks the roles up in LabOS itself and ignores any body, so this runs on
      // every sign-in — including for users with no roles, which is what clears
      // a role that was revoked. Uses "app_roles" so the global "role" claim
      // other apps depend on is left alone.
      try {
        const token = await fbUser.getIdToken();
        const response = await fetch('/api/v1/auth/custom-claims', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error(`Failed to sync custom claims: ${response.status}`);
        }

        console.log('[LabOS] App-specific custom claims synced');
        // Force token refresh to get new claims
        await fbUser.getIdToken(true);
      } catch (err) {
        console.error('[LabOS] Failed to sync custom claims:', err);
      }

      setState({
        user: {
          uid: fbUser.uid,
          email: fbUser.email,
          displayName: fbUser.displayName,
          role,
          isAdmin: role === 'admin',
          roles: userRoles,
        },
        firebaseUser: fbUser,
        config,
        loading: false,
      });
    });
  }, [appId]);

  const signOut = async () => {
    await auth.signOut();
    window.location.href = LABOS_URL;
  };

  if (state.loading) {
    return (
      <>
        {fallback || (
          <div
            style={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#fff',
              color: 'rgba(0,0,0,0.3)',
              fontSize: '14px',
              fontFamily: 'system-ui',
            }}
          >
            Authenticating...
          </div>
        )}
      </>
    );
  }

  return <Ctx.Provider value={{ ...state, signOut }}>{children}</Ctx.Provider>;
}

// Simple mock for development - provides context without errors
export function LabOSAuthMock({ children }: { children: ReactNode }) {
  const mockState: LabOSState = {
    user: null,
    firebaseUser: null,
    config: {},
    loading: false,
    signOut: async () => {
      console.log('[LabOS Mock] Sign out called');
    },
  };

  return <Ctx.Provider value={mockState}>{children}</Ctx.Provider>;
}

export function useLabOS(): LabOSState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useLabOS() must be inside <LabOSAuth>');
  return ctx;
}

export function LabOSTopbar({ appName, accentColor }: { appName: string; accentColor?: string }) {
  const { user, signOut } = useLabOS();
  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '40px',
        padding: '0 16px',
        background: '#fff',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        fontFamily: 'system-ui',
        fontSize: '12px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <a
          href={LABOS_URL}
          style={{ color: 'rgba(0,0,0,0.35)', textDecoration: 'none', fontWeight: 500, letterSpacing: '0.05em' }}
        >
          LabOS
        </a>
        <span style={{ color: 'rgba(0,0,0,0.12)' }}>/</span>
        <span style={{ color: accentColor || 'rgba(0,0,0,0.8)', fontWeight: 600 }}>{appName}</span>
      </div>
      <span
        style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '2px 8px',
          borderRadius: '4px',
          fontWeight: 600,
          fontSize: '10px',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: ENVIRONMENT === 'prod' ? '#b91c1c' : '#92400e',
          background: ENVIRONMENT === 'prod' ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.12)',
          border: `1px solid ${ENVIRONMENT === 'prod' ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)'}`,
        }}
      >
        {ENVIRONMENT}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span style={{ color: 'rgba(0,0,0,0.3)' }}>{user?.email}</span>
        <button
          onClick={signOut}
          style={{
            background: 'none',
            border: '1px solid rgba(0,0,0,0.08)',
            color: 'rgba(0,0,0,0.4)',
            padding: '2px 8px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '11px',
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
